import { useTranslation } from 'react-i18next';

import { getFragment } from '../graphql';
import {
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
    type PriorAuthorizationInfoFragment,
    PriorAuthorizationSource,
} from '../graphql/generated/graphql';

import { usePriorAuthorizationStatus } from './use-prior-authorization-status';

type Activity = {
    key: string;
    date: Date;
    status: string;
    description?: string;
    textButtonLabel?: string;
    onClick?: () => void;
    showChevron?: boolean;
};

export const usePriorAuthActivities = (
    priorAuthorization: PriorAuthorizationInfoFragment,
    onStatusClick: () => void,
    onSentToPharmacyClick?: () => void,
    pharmacyProvider?:
        | PharmacyProviderInfoFragment
        | MailOrderProviderInfoFragment
): Activity[] => {
    const { t } = useTranslation();
    const { priorAuthLinkText, statusText } = usePriorAuthorizationStatus(
        priorAuthorization?.status
    );

    const assignedPharmacyProvider =
        priorAuthorization?.assignedPharmacyProvider;
    const nonDrxPriorAuthorization =
        priorAuthorization.requestOrigin ===
        PriorAuthorizationSource.VendorInitiated;

    const provider = pharmacyProvider ?? getFragment(assignedPharmacyProvider);

    const activities: Activity[] = [
        {
            key: 'pa-status',
            date: new Date(priorAuthorization?.updatedAt),
            status: t('orderDetails.paStatus', { status: statusText }),
            textButtonLabel: priorAuthLinkText,
            onClick: onStatusClick,
            showChevron: true,
        },
    ];

    if (provider && !nonDrxPriorAuthorization) {
        activities.push({
            key: 'sent-to-pharmacy',
            date: new Date(priorAuthorization?.createdAt),
            status: t('orderDetails.sentToPharmacy'),
            description: provider.name,
            textButtonLabel: pharmacyProvider
                ? t('priorAuthorization.status.viewDetails')
                : undefined,
            onClick: pharmacyProvider ? onSentToPharmacyClick : undefined,
            showChevron: !!pharmacyProvider,
        });
    }

    return activities.sort((a, b) => b.date.getTime() - a.date.getTime());
};
